import { motion } from "framer-motion";
import {
  ShieldCheck,
  Wrench,
  Eye,
  Boxes,
  ArrowRightLeft,
  Plug,
  Check,
} from "lucide-react";

const REASONS = [
  {
    icon: ShieldCheck,
    title: "Service Profesional & Bergaransi",
    desc: "Setiap perangkat dikerjakan dengan standar kualitas terbaik dan melalui proses pengecekan yang teliti. Garansi service sebagai bentuk komitmen kami.",
  },
  {
    icon: Wrench,
    title: "Teknisi Handal & Berpengalaman",
    desc: "Perangkat Anda ditangani langsung oleh teknisi berpengalaman yang ahli dalam perbaikan Apple dan Android — dari kerusakan ringan hingga tingkat lanjut.",
  },
  {
    icon: Eye,
    title: "Transparan & Terpercaya",
    desc: "Kami selalu mengutamakan kejujuran dalam diagnosa dan proses pengerjaan, sehingga Anda mendapat informasi yang jelas mengenai kondisi perangkat dan estimasi biaya.",
  },
  {
    icon: Boxes,
    title: "Sparepart Berkualitas",
    desc: "Sparepart berkualitas untuk Apple dan Android guna menjaga performa dan ketahanan perangkat setelah diperbaiki.",
  },
  {
    icon: ArrowRightLeft,
    title: "Jual Beli Gadget Berkualitas",
    desc: "Layanan jual beli dan trade-in smartphone, tablet, dan gadget lainnya dengan proses pengecekan profesional.",
  },
  {
    icon: Plug,
    title: "Aksesoris Lengkap",
    desc: "Charger, kabel data, tempered glass, case, dan kebutuhan perangkat lainnya dengan kualitas terbaik.",
  },
];

const SERVICES_LIST = [
  "Service iPhone & Android",
  "Penggantian LCD & baterai",
  "Repair Face ID",
  "Repair Flexgate MacBook",
  "Perbaikan motherboard",
  "Software restore & update",
  "Backup & recovery data",
  "Jual beli & trade in gadget",
  "Sparepart Apple & Android",
  "Aksesoris gadget",
];

export default function AboutSection() {
  return (
    <section
      id="about"
      className="relative py-20 lg:py-28 bg-[#0A0A0A] text-white overflow-hidden"
      data-testid="about-section"
    >
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(#FFFFFF 1px, transparent 1px), linear-gradient(90deg, #FFFFFF 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="font-mono-ez text-[11px] font-bold tracking-[0.2em] uppercase text-[#FFD600] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FFD600]" />
              About Us
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]"
              data-testid="about-heading"
            >
              Pusat Service, Jual Beli Gadget,
              <br />
              <span className="text-[#FF3333]">Sparepart & Aksesoris</span>{" "}
              <span className="text-white">Terpercaya.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6 space-y-5 text-white/75 text-base lg:text-lg leading-relaxed">
            <p>
              <span className="text-white font-semibold">EZ Device</span> adalah
              perusahaan yang bergerak di bidang service profesional perangkat
              Apple dan Android, serta melayani jual beli gadget, sparepart, dan
              aksesoris berkualitas. Kami hadir untuk memberikan solusi lengkap
              bagi kebutuhan perangkat digital Anda — profesional, transparan,
              dan terpercaya.
            </p>
            <p>
              Dengan dukungan teknisi handal dan berpengalaman, setiap proses
              perbaikan dilakukan dengan standar pengerjaan terbaik dan disertai
              garansi service untuk memberikan rasa aman dan kepuasan kepada
              pelanggan.
            </p>
            <p>
              Kami menangani berbagai jenis kerusakan mulai dari penggantian
              LCD, baterai, Face ID, Flexgate MacBook, motherboard, hingga
              perbaikan kerusakan kompleks lainnya — dengan proses pengecekan
              yang detail dan transparan.
            </p>
          </div>
        </div>

        {/* Reasons grid */}
        <div className="mb-16">
          <div className="font-mono-ez text-[11px] font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-6">
            Kenapa memilih EZ Device
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="border border-white/15 p-6 hover:border-[#FF3333] hover:bg-white/5 transition-all group"
                data-testid={`about-reason-${i + 1}`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#FF3333] mb-5 group-hover:bg-[#FFD600] group-hover:text-[#0A0A0A] transition-colors">
                  <r.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight leading-tight mb-2">
                  {r.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">
                  {r.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services list + commitment */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="font-mono-ez text-[11px] font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-6">
              Layanan kami
            </div>
            <ul className="flex flex-wrap gap-2">
              {SERVICES_LIST.map((s) => (
                <li
                  key={s}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-sm font-medium hover:border-[#FFD600] hover:text-[#FFD600] transition-colors"
                >
                  <Check className="w-3.5 h-3.5 text-[#FFD600]" />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="font-mono-ez text-[11px] font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-6">
              Komitmen kami
            </div>
            <blockquote className="font-display text-2xl lg:text-3xl font-bold leading-tight tracking-tight">
              EZ Device berkomitmen memberikan{" "}
              <span className="text-[#FFD600]">layanan terbaik</span> dengan
              kualitas pengerjaan profesional, harga kompetitif, serta
              pelayanan yang{" "}
              <span className="text-[#FF3333]">terpercaya</span> demi kepuasan
              setiap pelanggan.
            </blockquote>
            <div className="mt-8 pt-6 border-t border-white/15">
              <div className="font-display text-4xl font-black tracking-tighter">
                EZ Device<span className="text-[#FF3333]">.</span>
              </div>
              <p className="font-mono-ez text-[11px] uppercase tracking-widest text-white/60 mt-2">
                Service Profesional · Bergaransi · Teknisi Handal · Jual Beli
                Gadget & Sparepart Berkualitas
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
